import {useState, useEffect, useMemo} from 'react';
import type NetworkOptimizationStore from '../../../lib/store';

interface UseProviderSelectionParams {
    networkOptimizationStore: NetworkOptimizationStore;
    existingProviders: any[];
}

interface UseProviderSelectionReturn {
    selection: string[];
    deselection: string[];
    selectionSet: Set<string>;
    deselectionSet: Set<string>;
    existingProviderIds: Set<string>;
    handleCheckboxChange: (
        rowId: string,
        checked: boolean,
        isExistingProvider: boolean
    ) => void;
}

export function useProviderSelection({
    networkOptimizationStore,
    existingProviders
}: UseProviderSelectionParams): UseProviderSelectionReturn {
    const [selection, setSelection] = useState<string[]>([]);
    const [deselection, setDeselection] = useState<string[]>([]);

    // Sync with store's selection state
    useEffect(() => {
        setSelection(networkOptimizationStore.selectedAdditionalProviderKeys);
        setDeselection(networkOptimizationStore.deselectedExistingProviderKeys);
    }, [networkOptimizationStore.selectedAdditionalProviderKeys, networkOptimizationStore.deselectedExistingProviderKeys]);

    // Convert to Sets for O(1) lookup performance with large datasets
    const selectionSet = useMemo(() => new Set(selection), [selection]);
    const deselectionSet = useMemo(() => new Set(deselection), [deselection]);
    const existingProviderIds = useMemo(() => new Set(existingProviders.map((p: any) => p.id)), [existingProviders]);

    // Handle checkbox change
    const handleCheckboxChange = (
        rowId: string,
        checked: boolean,
        isExistingProvider: boolean
    ) => {
        let operation = checked
            ? networkOptimizationStore.selectAdditionalProviders
            : networkOptimizationStore.deselectAdditionalProviders;

        if (isExistingProvider) {
            operation = checked
                ? networkOptimizationStore.selectExistingProviders
                : networkOptimizationStore.deselectExistingProviders;
        }

        operation.call(networkOptimizationStore, [rowId]);
    };

    return {
        selection,
        deselection,
        selectionSet,
        deselectionSet,
        existingProviderIds,
        handleCheckboxChange
    };
}
